import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { XeroLinkedService, xeroAuthType } from 'app/model/linkedservice/xero.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { xeroReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(XeroLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                host: '*',
                consumerKey: 'secret',
                privateKey: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    host: '*',
                    authenticationType: '*',
                    consumerKey: 'secret',
                    privateKey: 'secret',
                    useEncryptedEndpoints: '*',
                    useHostVerification: '*',
                    usePeerVerification: '*',
                    tenantId: '*',
                    refreshToken: 'secret',
                    encryptedCredential: '*'
                }
            }
        }
    }
})
export class XeroLinkedServiceAdapter extends LinkedServiceAdapter<XeroLinkedService> {
    protected modelConstructor(): new () => XeroLinkedService {
        return XeroLinkedService;
    }

    protected model(): IModel {
        return XeroLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: XeroLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.XeroLinkedService;
        properties.type = Metamodel.modelFor(XeroLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            linkedService.authType = xeroAuthType.OAuth_2_0;
            properties.typeProperties = this._buildTypeProperties(linkedService);

            if (linkedService.authType === xeroAuthType.OAuth_2_0) {
                properties.typeProperties.tenantId = linkedService.tenantId;
                properties.typeProperties.refreshToken = passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context);
            }

            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties[field.key] = field.value;
                });
            }
        } else if (linkedService.isOldResource) {
            properties.typeProperties = this._buildTypeProperties(linkedService);
        } else {
            properties.typeProperties = {
                connectionProperties: this._buildTypeProperties(linkedService)
            };

            if (linkedService.authType === xeroAuthType.OAuth_2_0) {
                properties.typeProperties.connectionProperties.tenantId = linkedService.tenantId;
                properties.typeProperties.connectionProperties.refreshToken = passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context);
            }

            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: XeroLinkedService): Promise<XeroLinkedService> {
        const properties = linkedServiceResource.properties as Model.XeroLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.XeroLinkedServiceTypeProperties = properties.typeProperties;

        linkedService.version = properties.version === CONNECTOR_VERSION20 ? driverVersions.v2 : driverVersions.v1;
        linkedService.isLegacyConnector = linkedService.version !== driverVersions.v2;
        if (linkedService.version === driverVersions.v2) {
            linkedService.isOldResource = false;
            linkedService = this._asssignLinkedServiceProperties(linkedService, typeProperties);

            linkedService.authType = typeProperties.authenticationType;
            if (linkedService.authType === xeroAuthType.OAuth_2_0) {
                linkedService.tenantId = typeProperties.tenantId;
                linkedService.refreshToken = secretBaseToPasswordOrAKV(typeProperties.refreshToken, true, this.context);
            }
            
            linkedService.additionalFields = [];
            Object.keys(typeProperties).forEach((key) => {
                if (xeroReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: typeProperties[key] });
                    delete typeProperties[key];
                }
            });
        } else if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            linkedService = this._asssignLinkedServiceProperties(linkedService, connectionProperties);

            linkedService.authType = connectionProperties.authenticationType;
            if (linkedService.authType === xeroAuthType.OAuth_2_0) {
                linkedService.tenantId = connectionProperties.tenantId;
                linkedService.refreshToken = secretBaseToPasswordOrAKV(connectionProperties.refreshToken, true, this.context);
            }

            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (xeroReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService = this._asssignLinkedServiceProperties(linkedService, typeProperties);
        }

        return linkedService;
    }

    private _buildTypeProperties(linkedService: XeroLinkedService) {
        return {
            host: linkedService.host,
            authenticationType: linkedService.authType,
            consumerKey: passwordOrAKVtoSecretBase(linkedService.consumerKey, this.context),
            privateKey: passwordOrAKVtoSecretBase(linkedService.privateKey, this.context),
            useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
            useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
            usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
        };
    }

    private _asssignLinkedServiceProperties(
        linkedService: XeroLinkedService,
        typeProperties: Model.XeroLinkedServiceTypeProperties
    ) {
        linkedService.host = typeProperties.host;
        linkedService.consumerKey = secretBaseToPasswordOrAKV(typeProperties.consumerKey, true, this.context);
        linkedService.privateKey = secretBaseToPasswordOrAKV(typeProperties.privateKey, true, this.context);
        linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
        linkedService.useHostVerification = typeProperties.useEncryptedEndpoints !== false ? typeProperties.useHostVerification : undefined;
        linkedService.usePeerVerification = typeProperties.useEncryptedEndpoints !== false ? typeProperties.usePeerVerification : undefined;

        return linkedService;
    }
}
